import { useEffect, useRef } from "react";

const vertexShader = /* glsl */ `
  uniform float time;
  uniform float amp;
  uniform float sep;
  uniform float sizeBase;
  uniform float sizeMul;
  uniform float pixelRatio;
  uniform vec2 distFade;   // far fade start / end
  uniform vec2 nearFade;   // near fade start / end
  uniform vec2 edgeFade;   // (0,0) = off, else half-extent in x / z to fade the grid ends over
  varying float vFade;
  void main() {
    vec3 p = position;
    float wx = sin((position.x / sep + time) * 0.3);
    float wz = sin((position.z / sep + time) * 0.5);
    p.y = (wx + wz) * amp;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    float dist = -mv.z;
    gl_PointSize = ((wx + 1.0) * 8.0 + (wz + 1.0) * 8.0 + sizeBase) * (sizeMul / dist) * pixelRatio;
    float fade = (1.0 - smoothstep(distFade.x, distFade.y, dist)) * smoothstep(nearFade.x, nearFade.y, dist);
    if (edgeFade.x > 0.0) fade *= 1.0 - smoothstep(0.45, 1.0, abs(position.x) / edgeFade.x);
    if (edgeFade.y > 0.0) fade *= 1.0 - smoothstep(0.45, 1.0, abs(position.z) / edgeFade.y);
    vFade = fade;
    gl_Position = projectionMatrix * mv;
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec3 color;
  uniform float opacity;
  varying float vFade;
  void main() {
    if (length(gl_PointCoord - vec2(0.5)) > 0.475) discard;
    gl_FragColor = vec4(color, vFade * opacity);
  }
`;

const DEFAULTS = {
  color: 0x5c5c5c,
  opacity: 1,
  fov: 50,
  cameraPos: [0, 500, 2300],
  lookAt: [0, 0, 0],
  amp: 60,
  speed: 0.6, // wave units per second
  sep: 100,
  countX: 50,
  countZ: 50,
  sizeBase: 4,
  sizeMul: 900,
  distFade: [3200, 6200],
  nearFade: [600, 1200],
  edgeFade: [0, 0],
  mouse: [1.2, 0.5], // camera travel per pixel of pointer offset (x, y)
  ease: 0.04, // camera follow easing per frame
  pointer: "mount", // measure pointer offset from the mount's centre, or "window"
  idle: null, // pointer offset (px from centre) to rest at on screens with no hover, where nothing ever moves the camera
  refFov: null, // { fov, height }: keep the on-screen dot scale of a reference canvas of that height
  rotation: [0, 0, 0],
};

/**
 * Renders the animated dot-wave into the returned ref's element.
 * `config` should be a module-level constant (read once on mount).
 */
export default function useWaveScene(config) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    // three.js loads in its own chunk so it stays out of the main bundle
    let cancelled = false;
    let cleanup = null;
    import("./three.js").then((THREE) => {
      if (!cancelled) cleanup = mountWave(THREE, mount, { ...DEFAULTS, ...config });
    });
    return () => {
      cancelled = true;
      cleanup?.();
    };
    // config is a module-level constant per component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return mountRef;
}

function mountWave(THREE, mount, c) {

  const width = () => mount.clientWidth || window.innerWidth;
  const height = () => mount.clientHeight || window.innerHeight;
  // 1.5 keeps the dots crisp while cutting the fill cost of a full-viewport canvas on hi-dpi screens
  const pixelRatio = Math.min(window.devicePixelRatio, 1.5);

  const fovFor = () =>
    c.refFov
      ? 2 * THREE.MathUtils.radToDeg(Math.atan(Math.tan(THREE.MathUtils.degToRad(c.refFov.fov / 2)) * (height() / c.refFov.height)))
      : c.fov;
  const camera = new THREE.PerspectiveCamera(fovFor(), width() / height(), 1, 12000);
  camera.position.set(...c.cameraPos);
  const lookAt = new THREE.Vector3(...c.lookAt);
  const scene = new THREE.Scene();

  const positions = new Float32Array(c.countX * c.countZ * 3);
  let i = 0;
  for (let ix = 0; ix < c.countX; ix++) {
    for (let iz = 0; iz < c.countZ; iz++) {
      positions[i++] = ix * c.sep - (c.countX * c.sep) / 2;
      positions[i++] = 0;
      positions[i++] = iz * c.sep - (c.countZ * c.sep) / 2;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      time: { value: 0 },
      amp: { value: c.amp },
      sep: { value: c.sep },
      sizeBase: { value: c.sizeBase },
      sizeMul: { value: c.sizeMul },
      pixelRatio: { value: pixelRatio },
      distFade: { value: new THREE.Vector2(...c.distFade) },
      nearFade: { value: new THREE.Vector2(...c.nearFade) },
      edgeFade: { value: new THREE.Vector2(...c.edgeFade) },
      opacity: { value: c.opacity },
      // raw sRGB values: the shader writes straight to the canvas, no colour-space conversion
      color: { value: new THREE.Color().setHex(c.color, THREE.LinearSRGBColorSpace) },
    },
    vertexShader,
    fragmentShader,
  });
  const points = new THREE.Points(geometry, material);
  points.rotation.set(...c.rotation);
  scene.add(points);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(pixelRatio);
  renderer.setSize(width(), height());
  mount.appendChild(renderer.domElement);

  // Pointer parallax (window-level so it works under overlaid text)
  let mouseX = 0;
  let mouseY = 0;
  // touch screens have no pointer to tilt the camera, so they rest at the pose a desktop pointer gives
  // in the top-left corner (a tap's synthetic mousemove is ignored, so the view doesn't jump around)
  const resting = !!c.idle && window.matchMedia("(hover: none), (pointer: coarse)").matches;
  if (resting) {
    [mouseX, mouseY] = c.idle;
    camera.position.x = c.cameraPos[0] + mouseX * c.mouse[0];
    camera.position.y = c.cameraPos[1] - mouseY * c.mouse[1];
  }
  const onMove = (e) => {
    if (c.pointer === "window") {
      mouseX = e.clientX - window.innerWidth / 2;
      mouseY = e.clientY - window.innerHeight / 2;
      return;
    }
    const r = mount.getBoundingClientRect();
    mouseX = e.clientX - r.left - r.width / 2;
    mouseY = e.clientY - r.top - r.height / 2;
  };
  if (!resting) window.addEventListener("mousemove", onMove);

  let lastW = width();
  let lastH = height();
  let resizeFrame = 0;
  const applyResize = () => {
    resizeFrame = 0;
    const w = width();
    const h = height();
    if (w === lastW && h === lastH) return;
    lastW = w;
    lastH = h;
    camera.aspect = w / h;
    camera.fov = fovFor();
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  };
  const onResize = () => {
    if (!resizeFrame) resizeFrame = requestAnimationFrame(applyResize);
  };
  // observe the mount, not the window: the hero frame also changes width on scroll
  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(mount);

  let visible = true;
  const observer = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
  });
  observer.observe(mount);

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const timer = new THREE.Timer();
  let frame = 0;
  const animate = (now) => {
    frame = requestAnimationFrame(animate);
    timer.update(now);
    if (!visible) return;
    const dt = Math.min(timer.getDelta(), 0.1);
    if (!reduceMotion) material.uniforms.time.value += dt * c.speed;
    camera.position.x += (c.cameraPos[0] + mouseX * c.mouse[0] - camera.position.x) * c.ease;
    camera.position.y += (c.cameraPos[1] - mouseY * c.mouse[1] - camera.position.y) * c.ease;
    camera.lookAt(lookAt);
    renderer.render(scene, camera);
  };
  animate();

  return () => {
    cancelAnimationFrame(frame);
    cancelAnimationFrame(resizeFrame);
    observer.disconnect();
    window.removeEventListener("mousemove", onMove);
    resizeObserver.disconnect();
    geometry.dispose();
    material.dispose();
    renderer.dispose();
    renderer.domElement.remove();
  };
}
